/**
 * @file 学员充值记录弹窗
 * @description 按学员拉取点数变更记录，一次展示最近 50 条。
 *
 * @whenToUse 学员列表行操作「充值记录」。
 * @whenNotToUse 校区充值记录（用 CampusRechargeRecordsDialog）。
 */

import { useEffect, useState } from 'react'
import { fetchStudentRechargeRecords, type StudentRechargeRecord } from '@/api/student.ts'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx'
import type { StudentListRow } from '@/components/pages/student-page/types.ts'

export type StudentRechargeRecordsDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 学员维度展平行 */
  student: StudentListRow | null
}

/**
 * 充值记录列表（打开时按学员拉取，一次展示最近 50 条）
 */
export function StudentRechargeRecordsDialog({
  open,
  onOpenChange,
  student,
}: StudentRechargeRecordsDialogProps) {
  const [records, setRecords] = useState<StudentRechargeRecord[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !student) return
    setLoading(true)
    fetchStudentRechargeRecords({
      studentId: student.studentId,
      pageNum: 1,
      pageSize: 50,
    })
      .then(({ rows }) => setRecords(rows))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false))
  }, [open, student])

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${student?.studentName ?? ''} · 充值记录`}
      className="max-w-2xl"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          关闭
        </Button>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>时间</TableHead>
            <TableHead>变更前</TableHead>
            <TableHead>变更后</TableHead>
            <TableHead>变动</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableEmpty colSpan={4} title="加载中…" />
          ) : records.length === 0 ? (
            <TableEmpty colSpan={4} title="暂无充值记录" />
          ) : (
            records.map((record) => {
              const delta = record.afterPoints - record.beforePoints
              return (
                <TableRow key={record.id}>
                  <TableCell>{record.createTime}</TableCell>
                  <TableCell>{record.beforePoints}</TableCell>
                  <TableCell>{record.afterPoints}</TableCell>
                  <TableCell className={delta >= 0 ? 'text-success' : 'text-danger'}>
                    {delta > 0 ? `+${delta}` : delta}
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </Dialog>
  )
}
